import React, { useState } from "react";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KIND_LABELS, apiError } from "@/lib/marketing";
import { CampaignCheckbox } from "@/components/admin/marketing/CampaignCheckbox";

const empty = { kind: "tip", title: "", summary: "", texts: [{ label: "Post", body: "" }], instructions: "", videos: [], asset_ids: [], send_email: false };

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-bold uppercase tracking-wide text-gray-500">{label}</span><div className="mt-1">{children}</div></label>
);

export const ContentEditor = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(() => initial
    ? { kind: initial.kind, title: initial.title, summary: initial.summary || "", texts: initial.texts || [], instructions: initial.instructions || "", videos: initial.videos || [], asset_ids: initial.asset_ids || [], send_email: !!initial.email_requested }
    : empty);
  const [assets, setAssets] = useState(initial?.assets || []);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setRow = (k, i, key, v) => set(k, f[k].map((r, j) => (j === i ? { ...r, [key]: v } : r)));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/admin/marketing/assets", fd);
      setAssets((a) => [...a, r.data]);
      set("asset_ids", [...f.asset_ids, r.data.id]);
      toast.success("File caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito");
    } finally { setBusy(false); }
  };

  const removeAsset = (id) => { setAssets((a) => a.filter((x) => x.id !== id)); set("asset_ids", f.asset_ids.filter((x) => x !== id)); };

  const save = async () => {
    setBusy(true);
    try {
      const r = initial ? await api.put(`/admin/marketing/contents/${initial.id}`, f) : await api.post("/admin/marketing/contents", f);
      toast.success("Contenuto salvato");
      onSaved(r.data);
    } catch (err) {
      toast.error(apiError(err, "Controlla i campi obbligatori"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="content-editor">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#0A0A0A]">{initial ? "Modifica contenuto" : "Nuovo contenuto"}</h2>
          <button onClick={onClose} data-testid="editor-close"><X size={20} /></button>
        </div>
        <Field label="Tipo">
          <select className="vg-input" value={f.kind} onChange={(e) => set("kind", e.target.value)} data-testid="editor-kind">
            {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Titolo"><input className="vg-input" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" /></Field>
        <Field label="Sintesi (anche nell'email)"><textarea className="vg-input min-h-[70px]" value={f.summary} onChange={(e) => set("summary", e.target.value)} data-testid="editor-summary" /></Field>
        <p className="text-xs text-gray-500 bg-[#FAFAFA] border border-gray-100 rounded-[8px] p-3">Scrivi <code className="font-mono text-[#B40101]">{"{LINK}"}</code> dove vuoi inserire il link referral personale di ciascun PA.</p>
        <div className="space-y-3">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Testi pronti</span>
          {f.texts.map((t, i) => (
            <div key={i} className="border border-gray-200 rounded-[10px] p-3 space-y-2" data-testid={`editor-text-${i}`}>
              <div className="flex gap-2">
                <input className="vg-input text-sm" placeholder="Etichetta (es. Post Instagram)" value={t.label} onChange={(e) => setRow("texts", i, "label", e.target.value)} data-testid={`editor-text-label-${i}`} />
                <button onClick={() => set("texts", f.texts.filter((_, j) => j !== i))} className="text-gray-400 hover:text-[#B40101]" data-testid={`editor-text-remove-${i}`}><Trash2 size={16} /></button>
              </div>
              <textarea className="vg-input text-sm min-h-[90px]" value={t.body} onChange={(e) => setRow("texts", i, "body", e.target.value)} data-testid={`editor-text-body-${i}`} />
            </div>
          ))}
          <button onClick={() => set("texts", [...f.texts, { label: "", body: "" }])} className="vg-btn-secondary flex items-center gap-1 px-3 py-1.5 text-xs" data-testid="editor-add-text"><Plus size={13} /> Aggiungi testo</button>
        </div>
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Immagini e PDF (max 20 MB)</span>
          <div className="flex flex-wrap gap-2">
            {assets.map((a) => (
              <span key={a.id} className="flex items-center gap-1 text-xs bg-[#F5F5F5] rounded-full px-3 py-1" data-testid={`editor-asset-${a.id}`}>
                {a.filename}<button onClick={() => removeAsset(a.id)}><X size={12} /></button>
              </span>
            ))}
          </div>
          <label className={`vg-btn-secondary inline-flex items-center gap-1 px-3 py-1.5 text-xs cursor-pointer ${busy ? "opacity-50" : ""}`}>
            <Upload size={13} /> Carica file
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" className="hidden" onChange={upload} disabled={busy} data-testid="editor-upload" />
          </label>
        </div>
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Video (link esterni: YouTube, Vimeo, Drive)</span>
          {f.videos.map((v, i) => (
            <div key={i} className="flex gap-2" data-testid={`editor-video-${i}`}>
              <input className="vg-input text-sm w-1/3" placeholder="Etichetta" value={v.label} onChange={(e) => setRow("videos", i, "label", e.target.value)} data-testid={`editor-video-label-${i}`} />
              <input className="vg-input text-sm flex-1" placeholder="https://…" value={v.url} onChange={(e) => setRow("videos", i, "url", e.target.value)} data-testid={`editor-video-url-${i}`} />
              <button onClick={() => set("videos", f.videos.filter((_, j) => j !== i))} className="text-gray-400 hover:text-[#B40101]"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => set("videos", [...f.videos, { label: "", url: "" }])} className="vg-btn-secondary flex items-center gap-1 px-3 py-1.5 text-xs" data-testid="editor-add-video"><Plus size={13} /> Aggiungi video</button>
        </div>
        <Field label="Istruzioni d'uso"><textarea className="vg-input min-h-[90px]" value={f.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="editor-instructions" /></Field>
        <CampaignCheckbox item={initial} sendAt={initial?.status === "scheduled" ? initial.publish_at : null}
          checked={f.send_email} onChange={(v) => set("send_email", v)} testid="editor-campaign" />
        {initial?.status === "published" && f.send_email && !initial.campaign && (
          <p className="text-xs text-gray-500" data-testid="editor-campaign-now-note">Contenuto già pubblicato: salvando, la campagna email parte subito e conta nel mese corrente.</p>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button onClick={onClose} className="vg-btn-secondary px-4 py-2 text-sm">Annulla</button>
          <button onClick={save} disabled={busy} className="vg-btn-primary px-5 py-2 text-sm" data-testid="editor-save">{initial && initial.status !== "draft" ? "Salva" : "Salva bozza"}</button>
        </div>
      </div>
    </div>
  );
};
